import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import '../assets/styles/TrialPolicy.css';

const TrialPolicy: React.FC = () => {
  return (
    <div className="trial-policy-page">
      <Helmet>
        <title>Trial Policy | Price on Rails | Translate Stocks</title>
        <meta 
          name="description" 
          content="Learn about our 15-day trial policy for Price on Rails. Try our AI-powered trading platform for just $150, which can be applied as credit toward a full subscription."
        />
      </Helmet>

      <div className="trial-policy-hero">
        <div className="container">
          <div className="trial-policy-header">
            <h1>15-Day Trial Policy</h1>
            <p className="trial-policy-subtitle">
              Experience the power of Price on Rails before committing to a full subscription
            </p>
          </div>
        </div>
      </div>

      <div className="trial-policy-content">
        <div className="container">
          <div className="policy-card">
            <div className="policy-section">
              <h2>Trial Overview</h2>
              <p>
                We offer a 15-day trial of Price on Rails for $150. This trial allows you to experience our platform's capabilities and determine if it's right for your trading strategy.
              </p>
              <div className="highlight-box">
                <div className="highlight-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#2563eb"/>
                  </svg>
                </div>
                <div className="highlight-content">
                  <p><strong>Your $150 trial fee will be applied as credit</strong> toward any full plan purchase made within 7 days after your trial ends.</p>
                </div>
              </div>
            </div>

            <div className="policy-section">
              <h2>Trial Limitations</h2>
              <p>
                During your trial period, you'll have access to most platform features with the following limitations:
              </p>
              <ul className="policy-list">
                <li><strong>Paper Trading Only:</strong> Broker connections will only be linked to paper trading accounts, not real trading accounts. The AI Trader Agent Bot will only perform simulated paper trades during the trial period.</li>
                
              </ul>
              <p>
                These limitations are in place to ensure system resources for our paid subscribers while still giving you a comprehensive experience of the platform's capabilities. <strong>While automatic trading is limited to paper accounts, you can still use the platform's analysis to make manual trades through your broker if you choose.</strong>
              </p>
            </div>

            <div className="policy-section">
              <h2>Important Trial Terms</h2>
              <div className="terms-grid">
                <div className="term-item">
                  <div className="term-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm4 0h-2v-6h2v6zm-1-9.21c-.69 0-1.25-.56-1.25-1.25S14.31 5.29 15 5.29s1.25.56 1.25 1.25-.56 1.25-1.25 1.25z" fill="#2563eb"/>
                    </svg>
                  </div>
                  <div className="term-content">
                    <h3>One Trial Per Person</h3>
                    <p>Only one trial is allowed per person, regardless of email address or payment method. No exceptions will be made to this policy.</p>
                  </div>
                </div>
                
                <div className="term-item">
                  <div className="term-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm4 0h-2v-6h2v6zm-1-9.21c-.69 0-1.25-.56-1.25-1.25S14.31 5.29 15 5.29s1.25.56 1.25 1.25-.56 1.25-1.25 1.25z" fill="#2563eb"/>
                    </svg>
                  </div>
                  <div className="term-content">
                    <h3>No Refunds for Trial Fee</h3>
                    <p>The $150 trial fee is non-refundable. It can only be applied as credit toward a full plan purchase.</p>
                  </div>
                </div>
                
                <div className="term-item">
                  <div className="term-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm4 0h-2v-6h2v6zm-1-9.21c-.69 0-1.25-.56-1.25-1.25S14.31 5.29 15 5.29s1.25.56 1.25 1.25-.56 1.25-1.25 1.25z" fill="#2563eb"/>
                    </svg>
                  </div>
                  <div className="term-content">
                    <h3>Trial Purpose</h3>
                    <p>The trial is intended to demonstrate the system's functionality and capabilities. It is not intended for users to generate profits to fund their full subscription purchase.</p>
                  </div>
                </div>
                
                <div className="term-item">
                  <div className="term-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm4 0h-2v-6h2v6zm-1-9.21c-.69 0-1.25-.56-1.25-1.25S14.31 5.29 15 5.29s1.25.56 1.25 1.25-.56 1.25-1.25 1.25z" fill="#2563eb"/>
                    </svg>
                  </div>
                  <div className="term-content">
                    <h3>Account Verification</h3>
                    <p>We verify user identity to prevent multiple trial accounts. Attempts to create multiple trial accounts will result in immediate termination without refund.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="policy-section">
              <h2>Post-Trial Options</h2>
              <p>At the end of your 15-day trial period, you have the following options:</p>
              <ol className="numbered-list">
                <li>
                  <strong>Upgrade to a full plan:</strong> Your $150 trial fee will be applied as credit toward your purchase if completed within 7 days after trial end.
                </li>
                <li>
                  <strong>Let trial expire:</strong> If you choose not to continue, your access will automatically end after 15 days with no additional charges.
                </li>
              </ol>
              <p>There are no automatic renewals or charges after your trial period ends.</p>
            </div>

            <div className="policy-cta">
              <h3>Ready to Experience Price on Rails?</h3>
              <div className="cta-buttons">
                <a href="https://pay.translatestocks.com/b/6oEcPM0vD65wcMg3cW" className="btn btn-primary">Start Your Trial Now</a>
                <Link to="/pricing" className="btn btn-secondary">View Pricing Plans</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialPolicy; 